import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AGENCY_LINK_ROLE_LABELS, formatToLocal, withObjectParticle, type SiteAgencyLinkItem } from '@safetyhub/shared';
import { Badge, Button, Card, ConfirmModal } from '@safetyhub/ui';
import { useToastStore } from '../../../../stores/toast';
import { apiErrorMessage } from '../../../../lib/api';
import { agencySiteLinkApi, agencySiteLinkQueryKeys } from '../../../../lib/agencySiteLink';
import { UNLINK_REASON_LABEL } from '../../../../lib/agencySiteLinkView';

interface Props {
  companyId: string;
  siteId: string;
}

/**
 * 현장 상세의 「대행 기관」 섹션(P4 · O2·O3 사후 통제 · O4 종료).
 * - 업체(소유 조직): 현재·과거 대행 링크 전부 + 해제(`REVOKED_BY_OWNER`)
 * - 기관: 자기 링크만 + 종료(`CONTRACT_ENDED`)
 * 해제 버튼은 서버가 준 `canUnlink`만 따른다(현재 링크 ∧ (소유 조직 ∨ 그 기관) ∧ `site.manage`).
 * 보여 줄 링크가 0건이면 **섹션째** 그리지 않는다 — 대행을 쓰지 않는 현장에 빈 카드를 띄우지 않는다(플래그 `agency-site-link`
 * 게이트는 2026-11-01에 걷었다). 해제 실패는 토스트로 받고 확인 모달을 닫는다(막다른 길 방지).
 */
export function SiteAgencyLinksSection({ companyId, siteId }: Props) {
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<SiteAgencyLinkItem | null>(null);

  const queryKey = agencySiteLinkQueryKeys.siteLinks(companyId, siteId);
  const { data } = useQuery({
    queryKey,
    queryFn: () => agencySiteLinkApi.listSiteLinks(companyId, siteId),
    enabled: !!companyId && !!siteId,
    retry: false,
  });

  const unlink = useMutation({
    mutationFn: (linkId: string) => agencySiteLinkApi.unlink(companyId, siteId, linkId),
    onSuccess: () => {
      setTarget(null);
      showToast(data?.isOwner ? '대행 연결을 해제했습니다.' : '대행을 종료했습니다.', 'success');
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      setTarget(null);
      showToast(apiErrorMessage(err) ?? '대행 연결을 해제하지 못했습니다.', 'error');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  if (!data || data.links.length === 0) return null;

  const isOwner = data.isOwner;
  const current = data.links.filter((l) => l.isCurrent);
  const past = data.links.filter((l) => !l.isCurrent);

  return (
    <Card padding="xl" radius="lg" elevation="md" data-testid="site-agency-links-section">
      <h2 className="mb-1 text-lg font-medium text-ink-900">{isOwner ? '대행 기관' : '우리 대행'}</h2>
      <p className="mb-4 text-xs text-ink-500">
        {isOwner
          ? '이 현장에 연결된 대행 기관입니다. 해제하면 그 기관은 이 현장의 법정 서류를 더 이상 작성할 수 없습니다.'
          : '우리 조직이 이 현장에 대행으로 연결된 이력입니다.'}
      </p>

      {current.length === 0 ? (
        <p className="text-sm text-ink-500">현재 연결된 대행 기관이 없습니다.</p>
      ) : (
        <ul className="divide-y divide-line">
          {current.map((link) => (
            <li key={link.linkId} className="flex flex-wrap items-center justify-between gap-2 py-3">
              <div>
                <p className="text-sm font-medium text-ink-900">
                  {link.companyName}{' '}
                  <Badge tone="blue" size="sm">
                    {AGENCY_LINK_ROLE_LABELS[link.role]}
                  </Badge>
                </p>
                <p className="text-xs text-ink-500">
                  {formatToLocal(new Date(link.linkedAt))} 연결{link.linkedByName ? ` · ${link.linkedByName}` : ''}
                </p>
              </div>
              {link.canUnlink && (
                <Button size="sm" variant="secondary" onClick={() => setTarget(link)} disabled={unlink.isPending}>
                  {isOwner ? '연결 해제' : '대행 종료'}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {past.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-ink-600">지난 연결 {past.length}건</summary>
          <ul className="mt-2 divide-y divide-line">
            {past.map((link) => (
              <li key={link.linkId} className="py-2 text-xs text-ink-600">
                {link.companyName} · {AGENCY_LINK_ROLE_LABELS[link.role]} · {formatToLocal(new Date(link.linkedAt))} ~{' '}
                {link.unlinkedAt ? formatToLocal(new Date(link.unlinkedAt)) : '-'}
                {link.unlinkReason ? ` (${UNLINK_REASON_LABEL[link.unlinkReason]})` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}

      {target && (
        <ConfirmModal
          open
          danger
          title={isOwner ? '대행 연결을 해제할까요?' : '대행을 종료할까요?'}
          body={
            isOwner
              ? `${target.companyName}의 ${AGENCY_LINK_ROLE_LABELS[target.role]} 연결을 해제합니다. 그 기관에는 종료 시점까지의 법정 기록만 읽기 전용으로 남습니다.`
              : `이 현장의 ${withObjectParticle(AGENCY_LINK_ROLE_LABELS[target.role])} 종료합니다. 종료 뒤에는 종료 시점까지의 법정 기록만 읽기 전용으로 볼 수 있습니다.`
          }
          confirmLabel={isOwner ? '연결 해제' : '대행 종료'}
          busy={unlink.isPending}
          onConfirm={() => unlink.mutate(target.linkId)}
          onClose={() => {
            if (!unlink.isPending) setTarget(null);
          }}
        />
      )}
    </Card>
  );
}
